import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Mail,
  Building2,
  RefreshCw,
  Calendar,
  AlertCircle,
  TrendingUp,
} from "lucide-react";
import Loading from "../components/Loading";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const STATUS_OPTIONS = [
  { value: "all", label: "All Requests", color: "gray" },
  { value: "pending", label: "Pending", color: "amber" },
  { value: "reviewed", label: "Reviewed", color: "blue" },
  { value: "in_progress", label: "In Progress", color: "purple" },
  { value: "completed", label: "Completed", color: "green" },
  { value: "dismissed", label: "Dismissed", color: "gray" },
];

const getStatusColor = (status) => {
  const colors = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    reviewed: "bg-blue-100 text-blue-800 border-blue-200",
    in_progress: "bg-purple-100 text-purple-800 border-purple-200",
    completed: "bg-green-100 text-green-800 border-green-200",
    dismissed: "bg-gray-100 text-gray-800 border-gray-200",
  };
  return colors[status] || colors.pending;
};

const getStatusIcon = (status) => {
  switch (status) {
    case "pending":
      return <Clock className="w-4 h-4" />;
    case "reviewed":
      return <Eye className="w-4 h-4" />;
    case "in_progress":
      return <TrendingUp className="w-4 h-4" />;
    case "completed":
      return <CheckCircle className="w-4 h-4" />;
    case "dismissed":
      return <XCircle className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

const getSourceLabel = (source) => {
  if (source === "org_page_zero_state") return "No Programs";
  if (source === "org_page_no_active") return "No Active Programs";
  return source || "Unknown";
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ReviewRequests = () => {
  const { getAccessToken } = useAuth();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");

  useEffect(() => {
    fetchRequests();
  }, [selectedStatus]);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = getAccessToken();
      if (!token) return;

      const queryParams = new URLSearchParams();
      if (selectedStatus !== "all") {
        queryParams.append("status", selectedStatus);
      }

      const response = await fetch(
        `${API_BASE_URL}/admin/review-requests?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) throw new Error("Failed to fetch review requests");

      const data = await response.json();
      setRequests(data.requests || []);
    } catch (err) {
      console.error("Error fetching review requests:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateRequestStatus = async (id, status) => {
    try {
      setIsUpdating(true);
      const token = getAccessToken();
      if (!token) return;

      const body = { status };
      if (adminNotes) {
        body.adminNotes = adminNotes;
      }

      const response = await fetch(
        `${API_BASE_URL}/admin/review-requests/${id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) throw new Error("Failed to update request");

      await fetchRequests();
      setShowDetailModal(false);
      setAdminNotes("");
    } catch (err) {
      console.error("Error updating request:", err);
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const filteredRequests = requests.filter((request) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      request.email?.toLowerCase().includes(query) ||
      request.organizationName?.toLowerCase().includes(query) ||
      request.organizationId?.toLowerCase().includes(query) ||
      request.source?.toLowerCase().includes(query)
    );
  });

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setAdminNotes(request.adminNotes || "");
    setShowDetailModal(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-1">
            Review Requests
          </h1>
          <p className="text-[var(--text-tertiary)]">
            Org page &ldquo;ask us to review&rdquo; submissions from zero-state
            pages
          </p>
        </div>
        <button
          type="button"
          onClick={fetchRequests}
          className="btn-secondary flex items-center gap-2"
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {STATUS_OPTIONS.map((option) => {
          const count =
            option.value === "all"
              ? requests.length
              : requests.filter((r) => r.status === option.value).length;
          return (
            <button
              type="button"
              key={option.value}
              onClick={() => setSelectedStatus(option.value)}
              className={`card p-4 text-left transition-all ${
                selectedStatus === option.value
                  ? "ring-2 ring-[var(--primary-500)]"
                  : ""
              }`}
            >
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {count}
              </p>
              <p className="text-sm text-[var(--text-tertiary)]">
                {option.label}
              </p>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Search by email, organization, or source..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Requests Table */}
      <div className="card overflow-hidden">
        {filteredRequests.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--bg-tertiary)] flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-[var(--text-muted)]" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
              No review requests found
            </h3>
            <p className="text-[var(--text-tertiary)]">
              {searchQuery
                ? "Try adjusting your search terms"
                : "No review requests match the selected filter"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--bg-secondary)] border-b border-[var(--border-primary)]">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Email
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Source
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-primary)]">
                {filteredRequests.map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-[var(--bg-secondary)] transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[var(--text-muted)]" />
                        <div>
                          <p className="font-medium text-[var(--text-primary)]">
                            {request.organizationName || "Unknown Org"}
                          </p>
                          <p className="text-xs text-[var(--text-muted)] font-mono">
                            {request.organizationId?.substring(0, 8)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-[var(--text-secondary)]">
                        <Mail className="w-4 h-4" />
                        <span>{request.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-[var(--text-secondary)]">
                        {getSourceLabel(request.source)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
                          request.status,
                        )}`}
                      >
                        {getStatusIcon(request.status)}
                        {request.status === "in_progress"
                          ? "In Progress"
                          : request.status.charAt(0).toUpperCase() +
                            request.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[var(--text-secondary)]">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(request.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => openDetailModal(request)}
                        className="btn-secondary py-1.5 px-3 text-sm"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-[var(--bg-primary)] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--border-primary)]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[var(--text-primary)]">
                  Review Request Details
                </h2>
                <button
                  type="button"
                  onClick={() => setShowDetailModal(false)}
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Organization Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                    Organization
                  </label>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[var(--text-muted)]" />
                    <p className="text-[var(--text-primary)] font-medium">
                      {selectedRequest.organizationName ||
                        "Unknown Organization"}
                    </p>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] font-mono mt-1">
                    ID: {selectedRequest.organizationId}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                      Requester Email
                    </label>
                    <p className="text-[var(--text-primary)]">
                      {selectedRequest.email}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                      Source
                    </label>
                    <p className="text-[var(--text-primary)]">
                      {getSourceLabel(selectedRequest.source)}
                    </p>
                  </div>
                </div>

                {selectedRequest.message && (
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                      Message
                    </label>
                    <p className="text-[var(--text-secondary)] bg-[var(--bg-secondary)] p-3 rounded-lg">
                      {selectedRequest.message}
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                  <Calendar className="w-4 h-4" />
                  <span>Submitted {formatDate(selectedRequest.createdAt)}</span>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="border-t border-[var(--border-primary)] pt-4">
                <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                  Admin Notes
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this request..."
                  className="input w-full h-24 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="border-t border-[var(--border-primary)] pt-4">
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.status !== "reviewed" && (
                    <button
                      type="button"
                      onClick={() =>
                        updateRequestStatus(selectedRequest.id, "reviewed")
                      }
                      disabled={isUpdating}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Mark as Reviewed
                    </button>
                  )}

                  {selectedRequest.status !== "in_progress" && (
                    <button
                      type="button"
                      onClick={() =>
                        updateRequestStatus(selectedRequest.id, "in_progress")
                      }
                      disabled={isUpdating}
                      className="btn-secondary flex items-center gap-2"
                      style={{ background: "rgba(168, 85, 247, 0.15)" }}
                    >
                      <TrendingUp className="w-4 h-4" />
                      Start Working
                    </button>
                  )}

                  {selectedRequest.status !== "completed" && (
                    <button
                      type="button"
                      onClick={() =>
                        updateRequestStatus(selectedRequest.id, "completed")
                      }
                      disabled={isUpdating}
                      className="btn-success flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Mark as Completed
                    </button>
                  )}

                  {selectedRequest.status !== "dismissed" && (
                    <button
                      type="button"
                      onClick={() =>
                        updateRequestStatus(selectedRequest.id, "dismissed")
                      }
                      disabled={isUpdating}
                      className="btn-danger flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Dismiss
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => setShowDetailModal(false)}
                    className="btn-secondary ml-auto"
                    disabled={isUpdating}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewRequests;
